import { createContext, useState, useEffect } from "react";
import { AxiosError } from "axios";
import { IUserDataResponse } from "../../services/types/users";
import { LandingPageLayout } from "../../components/layouts";
import { userRoleEnum } from "../../utils/enums";
import { AppLoader } from "../../components/common";
import {
  initializeTeamsApp,
  retryInitializeTeamsApp,
  isLikelyTeamsHost,
  getContext,
} from "../../infrastructure/teams";
import usersService from "../../services/users.service";
import WarningPage from "../WarningPage";
import AppInstruction from "../Shared/AppInstruction";
import TeamsTechnicalErrorPage from "../TeamsTechnicalErrorPage";

type TeamsBootstrapState =
  | "initializing"
  | "loading-user"
  | "authenticated"
  | "outside-teams"
  | "technical-error";

interface IUserContext {
  userData: IUserDataResponse;
  setUserData: React.Dispatch<React.SetStateAction<IUserDataResponse>>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<IUserContext>({
  userData: {} as IUserDataResponse,
  setUserData: () => {},
  refreshUser: async () => {},
});

// TODO(diagnostics): temporary Teams context log — remove once the fix is confirmed.
async function logTeamsContext() {
  if (process.env.NODE_ENV === "production") return;
  try {
    const context = await getContext();
    console.info("[Teams SDK] context", {
      hostClientType: context.app.host.clientType,
      hostName: context.app.host.name,
      frameContext: context.page.frameContext,
    });
  } catch (error) {
    console.warn("[Teams SDK] getContext failed", error);
  }
}

export function UserContextProvider(props: { children: React.ReactNode }) {
  const [userData, setUserData] = useState({} as IUserDataResponse);
  const [bootstrapState, setBootstrapState] =
    useState<TeamsBootstrapState>("initializing");

  const applyUserProfile = (data: IUserDataResponse) => {
    const roleFromData = data.data.roles && data.data.roles[0];
    const savedWorkContext = localStorage.getItem("workContext");
    const baseRole = roleFromData ?? userRoleEnum.Employee;

    data.data.baseRole = Object.values(userRoleEnum).includes(baseRole as userRoleEnum)
      ? baseRole
      : userRoleEnum.Employee;

    if (!savedWorkContext || savedWorkContext !== data.data.baseRole) {
      localStorage.setItem("workContext", data.data.baseRole);
    }

    setUserData(data);
  };

  const refreshUser = async () => {
    const data = await usersService.getSelf();
    applyUserProfile(data);
  };

  const loadUser = async () => {
    setBootstrapState("loading-user");
    try {
      const data = await usersService.getSelf();
      applyUserProfile(data);
      setBootstrapState("authenticated");
    } catch (error) {
      // Auth/token/network errors (no HTTP response) or 401/403 are treated as
      // access problems. Outside Teams → WarningPage; inside Teams → technical error.
      // Business/server errors (toast already shown by the interceptor) → technical error with retry.
      const status = (error as AxiosError)?.response?.status;
      const authLike = !(error as AxiosError)?.response || status === 401 || status === 403;

      if (authLike && !isLikelyTeamsHost()) {
        setBootstrapState("outside-teams");
      } else {
        setBootstrapState("technical-error");
      }
    }
  };

  const bootstrap = async (isRetry = false) => {
    setBootstrapState("initializing");

    try {
      await (isRetry ? retryInitializeTeamsApp() : initializeTeamsApp());
    } catch (error) {
      console.error("[Teams SDK] initialization failed in UserContext", error);
      setBootstrapState(isLikelyTeamsHost() ? "technical-error" : "outside-teams");
      return;
    }

    logTeamsContext();
    await loadUser();
  };

  const checkLocation = () => window.location.pathname === "/quick-start";

  useEffect(() => {
    bootstrap();
  }, []);

  if (bootstrapState === "authenticated") {
    return (
      <UserContext.Provider value={{ userData, setUserData, refreshUser }}>
        {props.children}
      </UserContext.Provider>
    );
  }

  if (bootstrapState === "outside-teams") {
    return checkLocation() ? <AppInstruction /> : <WarningPage />;
  }

  if (bootstrapState === "technical-error") {
    return <TeamsTechnicalErrorPage onRetry={() => bootstrap(true)} />;
  }

  // "initializing" / "loading-user" — SDK ready does not mean the user is ready.
  return (
    <LandingPageLayout>
      <AppLoader />
    </LandingPageLayout>
  );
}

export default UserContext;
